import React, {useRef, useState} from 'react';
import {css, styled} from 'styled-components';
import {colors} from '../../styles/colors';
import {useFormatMsg} from 'utils/useFormatMsg';
import CommonDatePicker from './CommonDatePicker';
import moment from 'moment-timezone';
import {minmax} from 'utils/minmax';
import {useOutSideRef} from 'utils/useOutSideRef';

/**
 * 라벨 기간 컴포넌트의 Props 인터페이스
 * @property {any} [labelText] - 라벨 텍스트
 * @property {{start: string | Date; end: string | Date}} value - 기간 값
 * @property {any} [setValue] - 값 설정 함수
 * @property {boolean} [isColumn] - 세로 방향 여부
 * @property {string} [errorText] - 에러 텍스트
 * @property {React.CSSProperties} [errorTextStyle] - 에러 텍스트 스타일
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [labelInputWrapStyle] - 라벨 입력 래퍼 스타일
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {boolean} [isWide] - 넓은 화면 여부
 * @property {React.CSSProperties} [inputStyle] - 입력 스타일
 * @property {React.CSSProperties} [dateContainerStyle] - 날짜 컨테이너 스타일
 * @property {string} [formatForm] - 날짜 포맷 형식
 * @property {React.CSSProperties} [dateWrapStyle] - 날짜 래퍼 스타일
 * @property {{startMinDate?: Date; startMaxDate?: Date; endMinDate?: Date; endMaxDate?: Date}} [eachLimit] - 각 날짜 제한
 */
interface ILabelInputProps {
  labelText?: any;
  value: {
    start: string | Date;
    end: string | Date;
  };
  setValue?: any;
  isColumn?: boolean;
  errorText?: string;
  errorTextStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  labelInputWrapStyle?: React.CSSProperties;
  readOnly?: boolean;
  isWide?: boolean;
  inputStyle?: React.CSSProperties;
  dateContainerStyle?: React.CSSProperties;
  formatForm?: string;
  dateWrapStyle?: React.CSSProperties;
  eachLimit?: {
    startMinDate?: Date;
    startMaxDate?: Date;
    endMinDate?: Date;
    endMaxDate?: Date;
  };
}

/**
 * 기간 타입
 * 선택된 기간 타입을 정의합니다.
 */
type IPeriod = '' | 'start' | 'end';

/**
 * 라벨 기간 컴포넌트
 * 라벨과 함께 시작일과 종료일을 선택할 수 있는 컴포넌트입니다.
 * @param {ILabelInputProps} props - 라벨 기간 컴포넌트의 props
 * @returns {JSX.Element} 라벨 기간 컴포넌트
 */
const LabelPeriod = ({
  labelText,
  value,
  setValue,
  isColumn,
  labelStyle,
  errorText,
  errorTextStyle,
  labelInputWrapStyle,
  readOnly,
  isWide,
  inputStyle,
  dateContainerStyle,
  formatForm,
  dateWrapStyle,
  eachLimit,
}: ILabelInputProps) => {
  value = {
    start: value.start ? new Date(value.start) : '',
    end: value.end ? new Date(value.end) : '',
  };

  const label = useFormatMsg(labelText) || labelText;

  const placeholder = useFormatMsg('datePlaceholder');

  // 날짜 모달
  const datePickerRef = useRef<HTMLDivElement>(null);
  const datePickerContainerRef = useRef<HTMLDivElement>(null);
  const [onDatePicker, setOnDatePicker] = useState<IPeriod>('');

  useOutSideRef(datePickerContainerRef, () => setOnDatePicker(''));

  return (
    <LabelInputWrap $isColumn={isColumn} style={labelInputWrapStyle}>
      {label && (
        <Label htmlFor={label} style={labelStyle} $isColumn={isColumn}>
          {label}
        </Label>
      )}

      <DateWrap style={dateWrapStyle}>
        <DateContainer style={dateContainerStyle} ref={datePickerContainerRef}>
          <DateItemWrap>
            <DateField
              style={{
                borderColor:
                  onDatePicker === 'start'
                    ? colors.primary2
                    : colors.borderGray,
                cursor: readOnly ? 'default' : 'pointer',
                ...inputStyle,
              }}
              onClick={() => {
                if (!readOnly) {
                  setOnDatePicker((prev: IPeriod) =>
                    prev === 'start' ? '' : 'start',
                  );
                }
              }}>
              <Text
                style={{
                  color: value.start ? colors.black : colors.borderGray,
                }}>
                {value.start
                  ? moment(value.start).format(formatForm ?? 'YYYY-MM-DD')
                  : placeholder}
              </Text>
              <Img src="images/common/calender.png" />
            </DateField>
            {onDatePicker === 'start' ? (
              <DatePickerWrap
                style={{
                  width: isWide ? '100%' : '210%',
                  left: '0',
                }}>
                <CommonDatePicker
                  innerRef={datePickerRef}
                  setOpen={() => setOnDatePicker('')}
                  value={value[onDatePicker]}
                  setValue={(date: any) => {
                    setValue({
                      ...value,
                      [onDatePicker]: date,
                    });
                  }}
                  minDate={
                    eachLimit && eachLimit.startMinDate
                      ? eachLimit.startMinDate
                      : undefined
                  }
                  maxDate={
                    value.end
                      ? value.end
                      : eachLimit && eachLimit.startMaxDate
                      ? eachLimit.startMaxDate
                      : undefined
                  }
                />
              </DatePickerWrap>
            ) : null}
          </DateItemWrap>

          <span className="tilde">~</span>

          <DateItemWrap>
            <DateField
              style={{
                borderColor:
                  onDatePicker === 'end' ? colors.primary2 : colors.borderGray,
                cursor: readOnly ? 'default' : 'pointer',
                ...inputStyle,
              }}
              onClick={() => {
                if (!readOnly) {
                  setOnDatePicker((prev: IPeriod) =>
                    prev === 'end' ? '' : 'end',
                  );
                }
              }}>
              <Text
                style={{
                  color: value.end ? colors.black : colors.borderGray,
                }}>
                {value.end
                  ? moment(value.end).format(formatForm ?? 'YYYY-MM-DD')
                  : placeholder}
              </Text>

              <Img src="images/common/calender.png" />
            </DateField>
            {onDatePicker === 'end' ? (
              <DatePickerWrap
                style={{
                  width: isWide ? '100%' : '210%',
                  right: '0',
                }}>
                <CommonDatePicker
                  innerRef={datePickerRef}
                  setOpen={() => setOnDatePicker('')}
                  value={value[onDatePicker]}
                  setValue={(date: any) => {
                    setValue({
                      ...value,
                      [onDatePicker]: date,
                    });
                  }}
                  minDate={
                    value.start
                      ? value.start
                      : eachLimit && eachLimit.endMinDate
                      ? eachLimit.endMinDate
                      : undefined
                  }
                  maxDate={
                    eachLimit && eachLimit.endMaxDate
                      ? eachLimit.endMaxDate
                      : undefined
                  }
                />
              </DatePickerWrap>
            ) : null}
          </DateItemWrap>
        </DateContainer>
      </DateWrap>
    </LabelInputWrap>
  );
};

export default LabelPeriod;

const LabelInputWrap = styled.div<{$isColumn?: boolean}>`
  width: 100%;
  height: ${minmax(60)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  ${(props: any) =>
    props.$isColumn &&
    css`
      flex-direction: column;
      height: auto;
    `}
`;

const Label = styled.label<{$isColumn?: boolean}>`
  font-size: ${minmax(20)};
  font-weight: 400;
  width: 20%;
  height: 100%;
  display: flex;
  justify-content: start;
  align-items: center;
  word-break: break-all;
  padding-right: 2%;
  ${(props: any) =>
    props.$isColumn &&
    css`
      width: 100%;
      margin-bottom: 12px;
    `}
`;

const DateWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  .tilde {
    width: 15%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: max(12px, min(1.5vw, 20px));
    font-weight: 400;
  }
`;

const DateItemWrap = styled.div`
  position: relative;
  width: 100%;
`;

const DateField = styled.div`
  width: 100%;
  padding-left: ${minmax(24)};
  padding-left: max(10px, min(1vw, 20px));
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  /* background-color: #f7f7f7; */ // 날짜 선택 안했을 때는 색깔 다르게? TODO! 디자인 확인 후 결정
  border-radius: ${minmax(10)};
  border: 1px solid ${colors.borderGray};
  cursor: pointer;
`;

const Text = styled.p`
  font-size: ${minmax(20)};
`;

const Img = styled.img`
  width: ${minmax(60)};
  height: ${minmax(60)};
  flex-shrink: 0;
`;

const DatePickerWrap = styled.div`
  width: 100%;
  position: absolute;
  top: 110%;
  z-index: 100;
`;

const DateContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  .tilde {
    width: 15%;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: max(12px, min(1.5vw, 20px));
    font-weight: 400;
  }
`;
